import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen min-h-dvh bg-gradient-to-br from-slate-50 via-slate-50/95 to-teal-50/30 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate("/")}
            className="mb-4 -ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="bg-white rounded-3xl shadow-lg border border-slate-200/60 p-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Terms of Service</h1>
            <p className="text-sm text-slate-500">Last Updated: December 26, 2025</p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-3xl shadow-lg border border-slate-200/60 p-8 space-y-6">
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">1. Acceptance of Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              By accessing and using DsaLens ("the Application"), you accept and agree to be bound by the terms and provisions of this agreement. This Application is intended exclusively for authorized Direct Selling Agents (DSA) partners and internal team members.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">2. Confidentiality and Non-Disclosure</h2>
            <div className="space-y-3 text-slate-600 leading-relaxed">
              <p className="font-semibold text-red-600">
                ⚠️ STRICTLY CONFIDENTIAL - INTERNAL USE ONLY
              </p>
              <p>
                This application and all its contents, including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Customer data and lead information</li>
                <li>Product details and pricing</li>
                <li>Business processes and workflows</li>
                <li>Interface designs and features</li>
                <li>Reports, analytics, and dashboards</li>
                <li>Communication templates and tools</li>
              </ul>
              <p className="font-semibold">
                are strictly confidential and proprietary. You agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>NOT take screenshots</strong> of any part of the application</li>
                <li><strong>NOT share</strong> any information, data, or content from this application with unauthorized individuals</li>
                <li><strong>NOT discuss</strong> application features, data, or processes outside the internal team</li>
                <li><strong>NOT record</strong> screen videos or make any digital copies of the interface</li>
                <li><strong>NOT photograph</strong> your screen while using the application</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">3. Authorized Use Only</h2>
            <p className="text-slate-600 leading-relaxed">
              Access to DsaLens is restricted to authorized DSA partners and internal team members only. You may not:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600 mt-3">
              <li>Share your login credentials with anyone</li>
              <li>Allow unauthorized individuals to access the application</li>
              <li>Use the application for any purpose other than authorized business activities</li>
              <li>Attempt to reverse engineer, decompile, or disassemble the application</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">4. Data Security</h2>
            <p className="text-slate-600 leading-relaxed">
              You are responsible for maintaining the security of your account and for all activities that occur under your account. You must immediately notify us of any unauthorized access or security breach.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">5. Intellectual Property</h2>
            <p className="text-slate-600 leading-relaxed">
              All content, features, and functionality of DsaLens are and will remain the exclusive property of DsaLens and its licensors. The application is protected by copyright, trademark, and other intellectual property laws.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">6. Consequences of Violation</h2>
            <p className="text-slate-600 leading-relaxed">
              Violation of these terms, particularly the confidentiality and non-disclosure provisions, may result in:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600 mt-3">
              <li>Immediate termination of access to the application</li>
              <li>Termination of your DSA partnership</li>
              <li>Legal action and liability for damages</li>
              <li>Criminal prosecution where applicable</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">7. Modifications to Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              We reserve the right to modify these terms at any time. Continued use of the application after changes constitutes acceptance of the modified terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">8. Contact Information</h2>
            <p className="text-slate-600 leading-relaxed">
              If you have any questions about these Terms of Service, please contact your team administrator.
            </p>
          </section>

          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-8">
            <p className="text-sm text-red-800 font-semibold">
              ⚠️ REMINDER: This is a confidential internal application. Screenshots, screen recordings, and sharing of any information from this application are strictly prohibited.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
